import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Eye, X, Save, Trash2, Copy, Check, Download, Home, UserPlus, Wallet, BadgeEuro } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import ConfirmModal from "@/components/ConfirmModal";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const deletableUsers = users.filter((u) => u.ruolo !== "admin");
  const allSelected = deletableUsers.length > 0 && deletableUsers.every((u) => selectedIds.has(u.id));

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(deletableUsers.map((u) => u.id)));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/users/${pendingDelete.id}`);
      setUsers((arr) => arr.filter((u) => u.id !== pendingDelete.id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(pendingDelete.id); return n; });
      if (selected?.id === pendingDelete.id) setSelected(null);
      toast.success("Utente eliminato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
    setPendingDelete(null);
  };

  const confirmBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      const delSet = new Set(r.data.deleted_ids || []);
      setUsers((arr) => arr.filter((u) => !delSet.has(u.id)));
      setSelectedIds(new Set());
      const skipped = (r.data.skipped || []).length;
      toast.success(`Eliminati ${r.data.deleted_count} utenti${skipped ? ` · ${skipped} saltati` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione in blocco");
    } finally {
      setBulkBusy(false);
      setBulkConfirm(false);
    }
  };

  return (
    <div className="space-y-6 fade-slide">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">Utenti</h1>
        <div className="flex items-center gap-3" data-testid="users-bulk-toolbar">
          <span className="text-sm text-gray-500" data-testid="users-selected-count">
            {selectedIds.size} selezionati
          </span>
          <button
            onClick={() => setBulkConfirm(true)}
            disabled={selectedIds.size === 0}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-[10px] bg-[#B40101] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#920101] transition-colors"
            data-testid="users-bulk-delete-btn"
          >
            <Trash2 size={15} /> Elimina selezionati
          </button>
        </div>
      </div>
      <div className="vg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Seleziona tutti"
                    data-testid="users-select-all"
                  />
                </th>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3">Ruolo</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Ref Attivi</th>
                <th className="px-4 py-3">Fee maturate €</th>
                <th className="px-4 py-3 text-left">Portato da</th>
                <th className="px-4 py-3">Iscritto</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-user-${u.id}`}>
                  <td className="px-4 py-3">
                    {u.ruolo !== "admin" ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => toggleOne(u.id)}
                        aria-label={`Seleziona ${u.email}`}
                        data-testid={`admin-user-select-${u.id}`}
                      />
                    ) : (
                      <span className="text-gray-300 text-xs" title="Account amministratore protetto">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#0A0A0A]">{u.nome} {u.cognome}</td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`vg-badge vg-badge-${u.ruolo}`}>{u.ruolo}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">{u.subscription_status}</td>
                  <td className="px-4 py-3 text-center">{u.lead_count}</td>
                  <td className="px-4 py-3 text-center">{u.referral_attivi}</td>
                  <td className="px-4 py-3 text-center" data-testid={`admin-user-fee-${u.id}`}>
                    <span className={`font-bold ${u.fee_da_pagare > 0 ? "text-[#B40101]" : "text-gray-400"}`}>
                      €{(u.fee_da_pagare ?? 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.portato_da_nome || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(u.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => setSelected(u)} className="vg-btn-secondary inline-flex items-center gap-1 text-xs px-3 py-2" data-testid={`admin-user-view-${u.id}`}>
                        <Eye size={14} /> Dettaglio
                      </button>
                      {u.ruolo !== "admin" && (
                        <button onClick={() => setPendingDelete(u)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors" data-testid={`admin-user-delete-${u.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <UserDetail
          user={selected}
          onClose={() => setSelected(null)}
          onDelete={selected.ruolo === "admin" ? null : () => setPendingDelete(selected)}
          onSaved={(updated) => {
            setUsers((arr) => arr.map((u) => u.id === updated.id ? { ...u, ...updated } : u));
            setSelected({ ...selected, ...updated });
          }}
        />
      )}

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina utente"
        message={pendingDelete ? (
          <span data-testid="user-delete-modal-body">
            Stai per eliminare definitivamente l'account:
            <span className="block mt-2 font-semibold text-[#0A0A0A]">
              {pendingDelete.nome} {pendingDelete.cognome} — {pendingDelete.email}
            </span>
            <span className="block mt-3">
              Verranno rimossi tutti i suoi dati collegati: casi immobiliari, valutazioni,
              decisioni e piani d'azione, richieste PA, accessi come professionista, lead,
              referral e abbonamenti. Gli altri utenti e i loro dati restano intatti.
            </span>
            <span className="block mt-3 font-semibold text-[#B40101]">
              L'operazione non è reversibile.
            </span>
          </span>
        ) : ""}
        confirmLabel="Conferma elimina"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testid="user-delete-modal"
      />

      <ConfirmModal
        open={bulkConfirm}
        title={`Elimina ${selectedIds.size} utenti selezionati`}
        message={(
          <span data-testid="users-bulk-modal-body">
            Stai per eliminare definitivamente <strong>{selectedIds.size}</strong> account
            selezionati e tutti i loro dati collegati (casi, valutazioni, decisioni, piani,
            richieste PA, accessi, lead, referral, abbonamenti).
            <span className="block mt-3">
              Gli account amministratori e il tuo account corrente vengono automaticamente
              esclusi. Gli altri utenti non coinvolti restano intatti.
            </span>
            <span className="block mt-3 font-semibold text-[#B40101]">
              L'operazione non è reversibile.
            </span>
          </span>
        )}
        confirmLabel={bulkBusy ? "Eliminazione…" : "Conferma elimina selezionati"}
        destructive
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkConfirm(false)}
        testid="users-bulk-delete-modal"
      />
    </div>
  );
}

function UserDetail({ user, onClose, onSaved, onDelete }) {
  const [notes, setNotes] = useState(user.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [copiedIban, setCopiedIban] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/notes`, { admin_notes: notes });
      onSaved({ id: user.id, admin_notes: notes });
      toast.success("Note salvate");
    } catch {
      toast.error("Errore salvataggio note");
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async () => {
    if (!window.confirm(`Confermi il pagamento di €${(user.fee_da_pagare ?? 0).toFixed(2)} a ${user.nome} ${user.cognome}?`)) return;
    setMarking(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/mark-fee-paid`);
      toast.success(`Pagato €${r.data.paid_amount.toFixed(2)}`);
      onSaved({ id: user.id, fee_da_pagare: 0, fee_paid_total: user.fee_maturata, last_fee_payment_at: r.data.payment_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setMarking(false);
    }
  };

  const copyIban = () => {
    if (!user.iban) return;
    navigator.clipboard.writeText(user.iban);
    setCopiedIban(true);
    toast.success("IBAN copiato");
    setTimeout(() => setCopiedIban(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[12px] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-xl font-bold text-[#0A0A0A]">Dettaglio utente</h3>
          <button onClick={onClose} data-testid="admin-user-detail-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Row label="Nome" value={`${user.nome} ${user.cognome}`} />
          <Row label="Email" value={user.email} />
          <Row label="Telefono" value={user.telefono || "—"} />
          <Row label="Agenzia" value={user.nome_agenzia || "—"} />
          <Row label="Ruolo" value={user.ruolo} />
          <Row label="Subscription" value={user.subscription_status} />
          <Row label="Referral code" value={user.referral_code} />
          <Row label="Portato da" value={user.portato_da_nome || "—"} />
          <Row label="Iscritto il" value={new Date(user.created_at).toLocaleString("it-IT")} />
          <Row label="Lead ricevuti" value={user.lead_count} />
          <Row label="Ref attivi" value={user.referral_attivi} />

          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-bold uppercase tracking-wide text-[#B40101] mb-2 flex items-center gap-2">
              <Wallet size={14} /> Fee e pagamenti
            </p>
            <Row label="Fee maturate totali" value={`€${(user.fee_maturata ?? 0).toFixed(2)}`} />
            <Row label="Fee già pagate" value={`€${(user.fee_paid_total ?? 0).toFixed(2)}`} />
            <Row label="Fee da pagare ora" value={
              <span className={`font-bold ${(user.fee_da_pagare ?? 0) > 0 ? "text-[#B40101]" : "text-gray-400"}`}>
                €{(user.fee_da_pagare ?? 0).toFixed(2)}
              </span>
            } />
            <Row label="Ultimo pagamento" value={user.last_fee_payment_at ? new Date(user.last_fee_payment_at).toLocaleDateString("it-IT") : "—"} />
            <div className="mt-3 p-3 rounded-[12px] bg-gray-50 border border-gray-200">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-wide text-[#0A0A0A]">IBAN</span>
                {user.iban && (
                  <button
                    className="vg-btn-secondary px-2 py-1 text-xs inline-flex items-center gap-1"
                    onClick={copyIban}
                    data-testid="admin-user-iban-copy"
                  >
                    {copiedIban ? <Check size={12} /> : <Copy size={12} />} Copia
                  </button>
                )}
              </div>
              <p className="font-mono text-sm text-[#0A0A0A] break-all" data-testid="admin-user-iban">
                {user.iban || <span className="text-gray-300 font-sans">Nessun IBAN configurato</span>}
              </p>
            </div>
            {(user.fee_da_pagare ?? 0) > 0 && (
              <button
                className="vg-btn-primary inline-flex items-center gap-2 mt-3"
                onClick={markPaid}
                disabled={marking}
                data-testid="admin-mark-fee-paid"
              >
                <BadgeEuro size={14} /> {marking ? "Salvataggio…" : `Segna come pagato (€${(user.fee_da_pagare ?? 0).toFixed(2)})`}
              </button>
            )}
          </div>

          {user.referral_code && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-bold uppercase tracking-wide text-[#B40101] mb-3">Link referral dell'agente</p>
              <ReferralBlock
                icon={<Home size={14} />}
                title="Link Proprietari"
                link={`https://pro.valutagenio.com/?ref=${user.referral_code}`}
                qrFilename={`valutagenio-${user.referral_code}-proprietari.png`}
                testid={`owner-${user.id}`}
              />
              <ReferralBlock
                icon={<UserPlus size={14} />}
                title="Link Agenti"
                link={`https://pro.valutagenio.com/signup?ref=${user.referral_code}`}
                qrFilename={`valutagenio-${user.referral_code}-agenti.png`}
                testid={`agent-${user.id}`}
              />
            </div>
          )}

          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-bold uppercase tracking-wide text-[#B40101] mb-2">Note admin (visibili solo all'admin)</p>
            <textarea
              className="vg-input min-h-[100px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Aggiungi note interne su questo utente..."
              data-testid="admin-user-notes-input"
            />
            <div className="mt-3 flex justify-between flex-wrap gap-3">
              <button className="vg-btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving} data-testid="admin-user-notes-save">
                <Save size={14} /> {saving ? "Salvataggio…" : "Salva note"}
              </button>
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-[12px] border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors text-sm font-semibold"
                  data-testid="admin-user-detail-delete"
                >
                  <Trash2 size={14} /> Elimina utente
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="text-[#0A0A0A] font-medium text-right break-all">{value}</span>
    </div>
  );
}

function ReferralBlock({ icon, title, link, qrFilename, testid }) {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copiato");
    setTimeout(() => setCopied(false), 2000);
  };
  const download = () => {
    const node = qrRef.current?.querySelector("canvas");
    if (!node) return;
    const url = node.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = qrFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR scaricato");
  };
  return (
    <div className="mt-3 p-3 rounded-[12px] bg-gray-50 border border-gray-200" data-testid={`ref-block-${testid}`}>
      <div className="flex items-center gap-2 mb-2 text-[#B40101]">
        {icon}
        <span className="text-xs font-bold uppercase tracking-wide text-[#0A0A0A]">{title}</span>
      </div>
      <div className="flex gap-2 mb-3">
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="vg-input text-xs font-mono flex-1 truncate hover:text-[#B40101]"
          style={{ display: "flex", alignItems: "center" }}
          data-testid={`ref-link-${testid}`}
        >
          {link}
        </a>
        <button
          className="vg-btn-secondary px-3 inline-flex items-center gap-1 text-xs"
          onClick={copy}
          data-testid={`ref-copy-${testid}`}
          title="Copia link"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <div className="flex items-center gap-3">
        <div ref={qrRef} className="p-2 bg-white rounded-[8px] border border-gray-200" data-testid={`ref-qr-${testid}`}>
          <QRCodeCanvas value={link} size={96} level="M" fgColor="#404040" bgColor="#FFFFFF" />
        </div>
        <button
          className="vg-btn-secondary inline-flex items-center gap-2 px-3 py-2 text-xs"
          onClick={download}
          data-testid={`ref-qr-download-${testid}`}
        >
          <Download size={12} /> Scarica QR
        </button>
      </div>
    </div>
  );
}
